import { daysBetween } from '../timeoff/timeoff';

/** The paper form's STEP 3 choices, word for word (Driver Portal keys). */
export const LOCATIONS: [string, string][] = [
  ['front_door', 'Front door'], ['back_door', 'Back door'], ['main_entrance', 'Main entrance/ front desk'], ['mail_room', 'Mail room'],
  ['side_door', 'Side door'], ['receiving', 'Receiving'], ['office_leasing', 'Office/leasing'], ['security', 'Security/ guard post'], ['other', 'Other'],
];
export const locationLabel = (key: string): string => LOCATIONS.find(([k]) => k === key)?.[1] ?? key;

export const TERM_YEARS = 2;
export const SOON_DAYS = 60;

export interface Op201Data {
  company: string; address: string; suite: string; city: string; state: string; zip: string; phone: string; email: string;
  start_date: string; location: string; location_other: string; instructions: string; print_name: string;
}
export const emptyOp201 = (today: string): Op201Data => ({
  company: '', address: '', suite: '', city: '', state: '', zip: '', phone: '', email: '',
  start_date: today, location: '', location_other: '', instructions: '', print_name: '',
});

export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Same list the database checks (and the plugin did). */
export function missingOp201(d: Op201Data, hasSignature: boolean): string[] {
  const m: string[] = [];
  if (!d.company.trim()) m.push('Company name');
  if (!d.address.trim()) m.push('Street address');
  if (!d.city.trim()) m.push('City');
  if (!d.state.trim()) m.push('State');
  if (!d.zip.trim()) m.push('ZIP');
  if (!d.phone.trim()) m.push('Phone');
  if (!EMAIL_RE.test(d.email.trim())) m.push('Customer email');
  if (!d.location) m.push('Secure location');
  if (d.location === 'other' && !d.location_other.trim()) m.push('Describe the “Other” location');
  if (!d.print_name.trim()) m.push('Printed name');
  if (!hasSignature) m.push('Signature');
  return m;
}

/** 2 years from the start date (what the server stores). */
export function expiresOn(start: string): string {
  const y = +start.slice(0, 4) + TERM_YEARS;
  const md = start.slice(4);
  // Feb 29 → Feb 28 when the target year isn't a leap year.
  return md === '-02-29' && !(y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0)) ? `${y}-02-28` : `${y}${md}`;
}

/** One row of "On file" (op201_on_file). */
export interface OnFile {
  id: string; company: string; address: string; city: string; state: string; zip: string; signer: string; location: string;
  start_date: string | null; expires_on: string | null; status: string; created_at: string; mine: boolean;
}

export type FileState = { label: string; tone: 'good' | 'warn' | 'bad' | 'muted'; renew: boolean };
/** Plugin badges: Active · Expires soon (60 days) · Expired · Renewed (superseded). */
export function fileState(r: Pick<OnFile, 'status' | 'expires_on'>, today: string): FileState {
  if (r.status === 'superseded') return { label: 'Renewed', tone: 'muted', renew: false };
  if (!r.expires_on) return { label: 'On file', tone: 'good', renew: false };
  const d = daysBetween(today, r.expires_on);
  if (d < 0) return { label: 'Expired', tone: 'bad', renew: true };
  if (d <= SOON_DAYS) return { label: `Expires in ${d} day${d === 1 ? '' : 's'}`, tone: 'warn', renew: true };
  return { label: 'Active', tone: 'good', renew: false };
}

/** Search by company, address, city or ZIP (case-insensitive). */
export const matches = (r: OnFile, q: string): boolean => {
  const s = q.trim().toLowerCase();
  return !s || [r.company, r.address, r.city, r.zip, r.signer].some((v) => (v ?? '').toLowerCase().includes(s));
};

export const fullAddress = (d: Pick<Op201Data, 'address' | 'suite' | 'city' | 'state' | 'zip'>): string =>
  [[d.address, d.suite].filter(Boolean).join(' '), [d.city, [d.state, d.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean).join(', ');


